import { isIosCompanionApp } from "@/lib/native/runtime";
import { canAccess, routeAccessRule } from "@/shared/access/store-access";
import {
  coreSettingsSections,
  isCoreSettingsStore,
  type CoreSettingsSectionId,
} from "@/shared/access/core-settings-sections";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import {
  apiBasePath,
  subscribeApiFetchClientResponses,
} from "@/lib/api/client";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { reinspectPrivateAccess } from "@/shared/services/privateSessionChange";
import { useStoreSwitch } from "@/shared/services/useStoreSwitch";
import { useStoreQuery } from "@/shared/services/useStoreQuery";
import { useOrderStatusCountsQuery } from "@/shared/orders/services/useOrderStatusCountsQuery";
import { selectNeedsYouCount } from "@/shared/orders/lib/needs-you";
import { useThemeStore } from "@/shared/theme/useTheme";
import type { SellerAuthView, SellerPermission } from "@/lib/api/types";
import { getSellerPath, getBuyerAbsoluteUrl } from "@/lib/routing/portal-urls";
import {
  classifySellerSessionFailure,
  getSellerLoginUrl,
} from "@/lib/auth/storeSlug";
import { PwaBootstrap } from "@/shared/pwa/components/PwaBootstrap";
import { InstallPrompt } from "@/shared/pwa/components/InstallPrompt";
import { FcmBootstrap } from "@/shared/notifications/components/FcmBootstrap";
import { FcmRegistrationProvider } from "@/shared/notifications/FcmRegistrationProvider";
import { NotificationPromptBanner } from "@/shared/notifications/components/NotificationPromptBanner";
import { NotificationBell } from "@/shared/notifications/components/NotificationBell";
import { BottomNav, useBottomNavTabs } from "./BottomNav";
import {
  isLostStoreCode,
  LOGIN_NOTICE_NO_STORE,
  sessionLossNoticeForCode,
} from "@/lib/auth/session-notice";
import { markFirstDelivered } from "@/shared/pwa/lib/interruption-order";
import { PersonalInfoModal } from "@/shared/profile/components/PersonalInfoModal";
import { PasswordModal } from "@/shared/profile/components/PasswordModal";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import { useQueryClient } from "@tanstack/react-query";
import {
  PullToRefreshHost,
  useDefaultPullToRefresh,
} from "@/shared/pull-to-refresh";
import {
  AccountMenu,
  Sidebar,
  type SidebarGroup,
  type SidebarSubItem,
} from "@a2z/ui";
import { Icons as SectionIcons } from "@/shared/store/primitives";
import {
  OnlineTransactionsTabIcon,
  WalletActivityIcon,
  WalletTabIcon,
} from "@/shared/payments/components/PaymentsTabNav";
import {
  DashboardSidebarIcon,
  OrdersSidebarIcon,
  CartsSidebarIcon,
  PaymentsSidebarIcon,
  CustomersSidebarIcon,
  ProductsSidebarIcon,
  CategoriesSidebarIcon,
  CouponsSidebarIcon,
  ReviewsSidebarIcon,
  SettingsSidebarIcon,
  ShareSidebarIcon,
  PlanSidebarIcon,
} from "./SidebarIcons";
import { NavbarSearch } from "@/shared/components/NavbarSearch";
import { ShareMenu } from "@/shared/components/ShareMenu";
import { useShareReadiness } from "@/shared/services/useShareReadiness";
import { ConfirmDialog } from "@a2z/ui";
import { PwaHead } from "@/shared/pwa/components/PwaHead";
import { PageChromeProvider, usePageChromeValues } from "./PageChromeContext";

import { useLogoutMutation } from "@/shared/services/useLogoutMutation";
import { locales } from "@/lib/i18n/config";
import { useFlag } from "@/shared/feature-flags/FeatureFlagsProvider";
import { MARKETING_ADS_FLAG } from "@/shared/feature-flags/flagKeys";

type AppShellProps = {
  locale: string;
  storeSlug: string;
  loginUrl?: string | null;
  children: React.ReactNode;
};

function isSellerApiPath(pathname: string): boolean {
  return pathname.startsWith(`${apiBasePath}/seller/`);
}

async function readApiErrorCode(response: Response): Promise<string | null> {
  try {
    const payload = (await response.clone().json()) as { code?: unknown };
    if (typeof payload?.code !== "string") {
      return null;
    }
    return payload.code;
  } catch {
    return null;
  }
}

export function AppShell({
  locale,
  storeSlug,
  loginUrl,
  children,
}: AppShellProps) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const t = useTranslations();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const scrollContainerRef = useRef<HTMLElement>(null);

  // App-wide pull-to-refresh fallback: any page that doesn't register its own
  // handler still refreshes by re-fetching its active data. `invalidateQueries`
  // with no filter marks every query stale and refetches the ones currently
  // mounted — i.e. exactly what the visible page is showing. Pages with more
  // specific needs (Orders, Dashboard, …) register via `usePullToRefresh` and
  // take precedence over this default.
  useDefaultPullToRefresh(() => queryClient.invalidateQueries());
  const sellerLoginPath = getSellerLoginUrl(locale);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () =>
      typeof window !== "undefined" &&
      localStorage.getItem("seller_sidebar_collapsed") === "1",
  );
  const toggleSidebar = () =>
    setSidebarCollapsed((prev) => {
      const next = !prev;
      if (typeof window !== "undefined") {
        localStorage.setItem("seller_sidebar_collapsed", next ? "1" : "0");
      }
      return next;
    });
  const [isSessionExpiryDialogOpen, setIsSessionExpiryDialogOpen] =
    useState(false);
  // Which account-menu modal (if any) is open. Driven from the avatar dropdown
  // on desktop, and from the minimal /profile screen on mobile.
  const [accountModal, setAccountModal] = useState<
    "personal-info" | "password" | null
  >(null);
  // Store share sheet — opened from the "Share store" nav entry on both the
  // desktop sidebar and the mobile menu.
  const [isShareOpen, setIsShareOpen] = useState(false);
  const didRedirectToLoginRef = useRef(false);
  // Set when the API said this actor lost the store; the redirect to login
  // that follows carries the reason.
  const lostStoreRef = useRef(false);
  const didOpenExpiryPromptRef = useRef(false);
  const { data: sessionData } = useSessionQuery({ storeSlug });
  const { data: storeData } = useStoreQuery({ storeSlug });
  const { isSwitching: isSwitchingStore, switchTo: switchStore } =
    useStoreSwitch(locale, storeSlug);
  const { data: orderStatusCounts } = useOrderStatusCountsQuery({
    storeSlug,
  });
  // The install sheet waits for the first delivered order (B7, rule 4 of
  // shared/pwa/lib/interruption-order.ts).
  const deliveredCount = orderStatusCounts?.counts?.delivered ?? 0;
  useEffect(() => {
    if (deliveredCount > 0) markFirstDelivered();
  }, [deliveredCount]);
  const resolvedDark = useThemeStore((s) => s.resolvedDark);
  const setThemePref = useThemeStore((s) => s.setPref);

  const storeIdentity = useMemo<Pick<
    SellerAuthView,
    "storeName" | "storeShort"
  > | null>(
    () =>
      sessionData
        ? {
            storeName: sessionData.seller.storeName,
            storeShort: sessionData.seller.storeShort,
          }
        : null,
    [sessionData],
  );

  // Drive the browser tab title from the store name: "<Store> - Trioty".
  useEffect(() => {
    const name = storeIdentity?.storeName?.trim();
    document.title = name ? `${name} - Trioty` : "Trioty";
  }, [storeIdentity]);

  const overview = sessionData?.overview ?? null;

  // The member's resolved permissions (from /seller/auth/me). Used to hide nav
  // entries a role can't use — a Support member shouldn't see Products/Coupons
  // management, etc. This mirrors the backend @RequirePermission gates; the UI
  // hide is convenience, the API is the real enforcement. Owner has every
  // permission so sees everything.
  const grantedPermissions = useMemo(
    () => new Set(sessionData?.seller.permissions ?? []),
    [sessionData?.seller.permissions],
  );
  // Missing session (still loading) → treat as "allowed" so nothing flickers
  // out during load; once the session resolves the real set applies.
  const can = (permission: SellerPermission): boolean =>
    grantedPermissions.has(permission);

  const canVisit = (href?: string) => !href || canAccess(sessionData?.seller, routeAccessRule(href.replace(`/${locale}/${storeSlug}/`, "")));

  // Derived order state for sidebar. This was hand-summed from two statuses and
  // silently omitted `pending_seller_confirmation` — the commonest one — so the
  // badge under-reported the seller's own work. It now reads THE definition,
  // the same one the dashboard's "needs you" line and the Orders chip render.
  const actionableOrderCount = selectNeedsYouCount(orderStatusCounts);

  const dashboardHref = getSellerPath(locale, storeSlug, "/dashboard");
  const storeSettingsHref = getSellerPath(locale, storeSlug, "/settings");
  const categoriesHref = getSellerPath(locale, storeSlug, "/categories");
  const productsHref = getSellerPath(locale, storeSlug, "/products");
  const ordersHref = getSellerPath(locale, storeSlug, "/orders");
  const metaHref = getSellerPath(locale, storeSlug, "/meta");
  const cartsHref = getSellerPath(locale, storeSlug, "/carts");
  const paymentsHref = getSellerPath(locale, storeSlug, "/payments");
  // Online transactions is no longer a page of its own — it is the Payments
  // page's third tab. The old `/transactions` route still resolves (it
  // redirects), but nothing in the nav points at it any more.
  const transactionsHref = `${paymentsHref}?tab=online`;
  const customersHref = getSellerPath(locale, storeSlug, "/customers");
  const reviewsHref = getSellerPath(locale, storeSlug, "/reviews");
  const couponsHref = getSellerPath(locale, storeSlug, "/coupons");
  const discountsHref = getSellerPath(locale, storeSlug, "/discounts");
  const freeShippingHref = getSellerPath(locale, storeSlug, "/free-shipping");
  const accountHref = getSellerPath(locale, storeSlug, "/account");
  const planHref = getSellerPath(locale, storeSlug, "/plan");
  // Storefront link shared from the nav's "Share store" entry. Computed here
  // (same helper the dashboard used) so the nav owns its own data instead of
  // threading a URL down through unrelated props.
  const storeUrl = getBuyerAbsoluteUrl(locale, storeSlug);
  // What a stranger arriving from that link would not find. Read here, next to
  // the URL, because the share sheet is the one moment the answer is actionable
  // — and from the session + store queries this shell already holds, so it adds
  // no request. Passed ONLY to the store share below; see the ShareMenu doc.
  const shareReadiness = useShareReadiness({ storeSlug, locale });
  const isDashboardActive = pathname?.startsWith(dashboardHref);
  const isStoreSettingsActive = pathname?.startsWith(storeSettingsHref);
  const isCategoriesActive = pathname?.startsWith(categoriesHref);
  const isProductsActive = pathname?.startsWith(productsHref);
  const isOrdersActive = pathname?.startsWith(ordersHref);
  const isCartsActive = pathname?.startsWith(cartsHref) ?? false;
  // Both rows now live on the same pathname, so the tab decides which one is
  // lit. Without this the Payments row would highlight while the seller is
  // reading Online transactions, and both rows would light at once.
  const onPayments = pathname?.startsWith(paymentsHref) ?? false;
  const currentPaymentsTab = new URLSearchParams(search).get("tab");
  const isTransactionsActive = onPayments && currentPaymentsTab === "online";
  const isPaymentsActive = onPayments && currentPaymentsTab !== "online";
  const isCustomersActive = pathname?.startsWith(customersHref) ?? false;
  const isReviewsActive = pathname?.startsWith(reviewsHref) ?? false;
  const isCouponsActive = pathname?.startsWith(couponsHref) ?? false;
  const isDiscountsActive = pathname?.startsWith(discountsHref) ?? false;
  const isFreeShippingActive = pathname?.startsWith(freeShippingHref) ?? false;
  useEffect(() => {
    return subscribeApiFetchClientResponses(
      async ({ requestUrl, response }) => {
        if (
          !isSellerApiPath(requestUrl.pathname) ||
          didRedirectToLoginRef.current
        ) {
          return;
        }

        const errorCode = await readApiErrorCode(response);
        const failureKind = classifySellerSessionFailure(
          response.status,
          errorCode,
        );
        if (!failureKind) {
          return;
        }

        if (failureKind === "access-change") {
          if (errorCode === "STORE_MEMBERSHIP_INACTIVE") lostStoreRef.current = true;
          // The identity cookie remains valid; clear old store data and inspect
          // current access so paused staff keep recovery and other-store access.
          // A lost FEATURE (capability) updates the access context in place so
          // unsaved work survives; a lost MEMBERSHIP clears store data first.
          await reinspectPrivateAccess(
            queryClient,
            errorCode === "STORE_CAPABILITY_REQUIRED" ? "capability" : "membership",
          ).catch(() => undefined);
          return;
        }

        if (failureKind === "expired") {
          if (!didOpenExpiryPromptRef.current) {
            didOpenExpiryPromptRef.current = true;
            setIsSessionExpiryDialogOpen(true);
          }
          return;
        }

        didRedirectToLoginRef.current = true;
        setIsSessionExpiryDialogOpen(false);
        // A removed member sees why (B10): the login's own sentence.
        // A deactivated store (403 STORE_DEACTIVATED) says exactly that; the
        // login page then signs the session out.
        const notice = sessionLossNoticeForCode(errorCode);
        const lostStore = lostStoreRef.current || isLostStoreCode(errorCode);
        navigate(
          notice
            ? `${sellerLoginPath}${notice}`
            : lostStore
              ? `${sellerLoginPath}?notice=${LOGIN_NOTICE_NO_STORE}`
              : sellerLoginPath,
          { replace: true },
        );
      },
    );
  }, [navigate, queryClient, sellerLoginPath]);

  // ─── Sidebar nav model ──────────────────────────────────────────────────────
  // Order + grouping per roundtable verdict (balanced 3/3/3/3):
  // .roundtable/roundtable-2026-06-03-sidebar-order-brief.md
  //
  // The editor's own active-state bookkeeping went with its nav entries: there
  // is nothing left to light up. It used to need TWO flags, because the simple
  // editor lives under the same path prefix and both rows would highlight at
  // once.
  // Store settings → section submenu (branding is the default / param-less URL).
  const adsEnabled = useFlag(MARKETING_ADS_FLAG);
  const onSettings = isStoreSettingsActive ?? false;
  const currentSection =
    new URLSearchParams(search).get("section") ?? "branding";
  // Each settings section maps to the permission that lets a member do anything
  // there. A Support member (STORE_VIEW only) sees no settings sections and the
  // whole Store-settings entry drops out below.
  // A Core (Free) store's settings page has its own, plainer sections; the
  // sub-menu lists exactly those, with the page's own labels and order, so the
  // sidebar never says "الهوية" for the chip that says "شعار المتجر".
  const coreSettingsIcons: Record<CoreSettingsSectionId, React.ReactNode> = {
    branding: SectionIcons.globe,
    payment: SectionIcons.card,
    shipping: SectionIcons.truck,
    domain: SectionIcons.link,
    team: SectionIcons.team,
    marketing: SectionIcons.megaphone,
    help: SectionIcons.help,
  };
  const fullSettingsSubItems: SidebarSubItem[] = (
    [
      {
        href: storeSettingsHref,
        label: t(keys.seller.storeSettings.sectionBrandingLabel),
        icon: SectionIcons.globe,
        isActive: onSettings && currentSection === "branding",
        permission: "STORE_EDIT",
      },
      {
        // «التصميم» — the storefront look and the design catalogue (Rule 23).
        // DESIGN_VIEW, not STORE_EDIT: the same gate the editor entry carries,
        // and for the same reason — the theme routes are gated on the design
        // family, so a MANAGER with STORE_EDIT would land on a panel whose
        // every query 403s into a global error toast.
        href: `${storeSettingsHref}?section=design`,
        label: t(keys.seller.storeSettings.sectionDesignLabel),
        icon: SectionIcons.palette,
        isActive: onSettings && currentSection === "design",
        permission: "DESIGN_VIEW",
      },
      {
        href: `${storeSettingsHref}?section=payment`,
        label: t(keys.seller.storeSettings.sectionPaymentLabel),
        icon: SectionIcons.card,
        isActive: onSettings && currentSection === "payment",
        permission: "PAYMENT_VIEW",
      },
      {
        href: `${storeSettingsHref}?section=shipping`,
        label: t(keys.seller.storeSettings.sectionShippingLabel),
        icon: SectionIcons.truck,
        isActive: onSettings && currentSection === "shipping",
        permission: "SHIPPING_VIEW",
      },
      {
        href: `${storeSettingsHref}?section=inventory`,
        label: t(keys.seller.storeSettings.sectionInventoryLabel),
        icon: SectionIcons.grid,
        isActive: onSettings && currentSection === "inventory",
        permission: "STORE_EDIT",
      },
      {
        href: `${storeSettingsHref}?section=policies`,
        label: t(keys.seller.storeSettings.sectionPoliciesLabel),
        icon: SectionIcons.doc,
        isActive: onSettings && currentSection === "policies",
        permission: "STORE_EDIT",
      },
      // Behind MARKETING_ADS, matching the settings page: no flag, no entry.
      ...(adsEnabled
        ? [
            {
              // Ad tracking ids are store-level configuration that changes what
              // the public storefront loads — same STORE_EDIT gate as branding.
              href: `${storeSettingsHref}?section=marketing`,
              label: t(keys.seller.storeSettings.sectionMarketingLabel),
        icon: SectionIcons.megaphone,
              isActive: onSettings && currentSection === "marketing",
              permission: "STORE_EDIT" as const,
            },
          ]
        : []),
      {
        href: `${storeSettingsHref}?section=team`,
        label: t(keys.seller.storeSettings.sectionTeamLabel),
        icon: SectionIcons.team,
        isActive: onSettings && currentSection === "team",
        permission: "MEMBER_VIEW",
      },
      {
        href: `${storeSettingsHref}?section=domain`,
        label: t(keys.seller.storeSettings.sectionDomainLabel),
        icon: SectionIcons.link,
        isActive: onSettings && currentSection === "domain",
        permission: "STORE_EDIT",
      },
      {
        // Help & guides is read-only educational content — every role that can
        // reach the store (STORE_VIEW) should be able to open it.
        href: `${storeSettingsHref}?section=help`,
        label: t(keys.seller.storeSettings.sectionHelpLabel),
        icon: SectionIcons.help,
        isActive: onSettings && currentSection === "help",
        permission: "STORE_VIEW",
      },
    ] satisfies (SidebarSubItem & { permission: SellerPermission })[]
  )
    .filter((sub) => can(sub.permission) && canVisit(sub.href))
    .map(({ permission: _permission, ...sub }) => sub);
  const settingsSubItems: SidebarSubItem[] = isCoreSettingsStore(sessionData?.seller)
    ? coreSettingsSections(sessionData?.seller, { adsEnabled }).map((section) => ({
        href:
          section.id === "branding"
            ? storeSettingsHref
            : `${storeSettingsHref}?section=${section.id}`,
        label: t(section.labelKey),
        icon: coreSettingsIcons[section.id],
        isActive: onSettings && currentSection === section.id,
      }))
    : fullSettingsSubItems;

  // Promotions → Coupons (code) + Discounts (automatic) + Free shipping (free
  // delivery, its own surface since it was removed from the kind picker). All
  // three share the coupon endpoint and the same COUPON_VIEW gate.
  const promotionsSubItems: SidebarSubItem[] = (
    [
      // Icons, not bullets: a dot only says "nested", which the indent and the
      // rail already say. Three promotion types are told apart by their glyph.
      {
        href: couponsHref,
        label: t(keys.coupons.navLabel),
        icon: <CouponsSidebarIcon />,
        isActive: isCouponsActive,
        permission: "COUPON_VIEW",
      },
      {
        href: discountsHref,
        label: t(keys.discounts.navLabel),
        icon: SectionIcons.megaphone,
        isActive: isDiscountsActive,
        permission: "COUPON_VIEW",
      },
      {
        href: freeShippingHref,
        label: t(keys.freeShipping.navLabel),
        icon: SectionIcons.truck,
        isActive: isFreeShippingActive,
        permission: "COUPON_VIEW",
      },
    ] satisfies (SidebarSubItem & { permission: SellerPermission })[]
  )
    .filter((sub) => can(sub.permission) && canVisit(sub.href))
    .map(({ permission: _permission, ...sub }) => sub);

  /**
   * Payments → its three tabs. Same shape as the settings sub-items above, and
   * for the same reason: one page whose sections are a `?` param, listed under
   * one nav entry rather than as sibling top-level rows.
   *
   * ONE gate, `PAYMENT_VIEW`, on the entry and on every sub-item. Merging the
   * standalone `/transactions` page into this page put two gates on one route
   * — the online page's `PAYMENT_VIEW` and the wallet's `STORE_EDIT` — and the
   * owner's call is to keep the online page's, the portal's money permission,
   * for all three tabs. That WIDENS the wallet to `PAYMENT_VIEW` members; the
   * former `STORE_EDIT` check on this entry is gone rather than left beside
   * it, so there is one answer to "who can see Payments?", not two.
   */
  const paymentsSubItems: SidebarSubItem[] = (
    [
      // The same glyphs the Payments page uses for these views — two rows
      // both starting "Wallet" are told apart by their icon, not their bullet.
      {
        href: paymentsHref,
        label: t(keys.payments.tabWallet),
        icon: <WalletTabIcon />,
        isActive: onPayments && currentPaymentsTab === null,
        permission: "PAYMENT_VIEW",
      },
      {
        href: `${paymentsHref}?tab=transactions`,
        label: t(keys.payments.tabTransactions),
        icon: <WalletActivityIcon />,
        isActive: onPayments && currentPaymentsTab === "transactions",
        permission: "PAYMENT_VIEW",
      },
      {
        href: transactionsHref,
        label: t(keys.transactions.navLabel),
        icon: <OnlineTransactionsTabIcon />,
        isActive: isTransactionsActive,
        permission: "PAYMENT_VIEW",
      },
    ] satisfies (SidebarSubItem & { permission: SellerPermission })[]
  )
    .filter((sub) => can(sub.permission) && canVisit(sub.href))
    .map(({ permission: _permission, ...sub }) => sub);

  // Each nav entry carries the VIEW permission that makes it usable. Entries
  // the member lacks are dropped, and a group with no remaining entries is
  // dropped whole. `null` = always shown (Dashboard is the landing page every
  // member can reach). Store settings shows if ANY of its sections survived.
  const rawGroups: (Omit<SidebarGroup, "items"> & {
    items: (SidebarGroup["items"][number] & {
      permission: SellerPermission | null;
    })[];
  })[] = [
    {
      header: t(keys.seller.navGroupStore),
      items: [
        {
          href: dashboardHref,
          label: t(keys.seller.dashboardTitle),
          icon: <DashboardSidebarIcon />,
          isActive: isDashboardActive ?? false,
          permission: null,
        },
        {
          href: ordersHref,
          label: t(keys.orders.title),
          icon: <OrdersSidebarIcon />,
          isActive: isOrdersActive ?? false,
          count: actionableOrderCount > 0 ? actionableOrderCount : undefined,
          // Tier 1's one accent, the same badge the phone's Orders tab draws.
          countTone: "accent",
          permission: "ORDER_VIEW",
        },
        {
          href: cartsHref,
          label: t(keys.carts.title),
          icon: <CartsSidebarIcon />,
          isActive: isCartsActive,
          permission: "ORDER_EDIT",
        },
        // Roundtable 2 (B8): the phone's bottom tabs, in the same order —
        // الرئيسية · الطلبات · المنتجات · العملاء — then the rest.
        {
          href: productsHref,
          label: t(keys.seller.productsTitle),
          icon: <ProductsSidebarIcon />,
          isActive: isProductsActive ?? false,
          // "Add your first product" — only for someone who can add one (an
          // order moderator cannot), like the dashboard's first-product prompt.
          showHint: overview && can("PRODUCT_CREATE") ? !overview.hasProducts : false,
          permission: "PRODUCT_VIEW",
        },
        {
          href: customersHref,
          label: t(keys.customers.navLabel),
          icon: <CustomersSidebarIcon />,
          isActive: isCustomersActive,
          // Customers are surfaced from orders; support reps (ORDER_VIEW) need them.
          permission: "ORDER_VIEW",
        },
        {
          // ONE money destination. The gateway transactions row that used to
          // sit here is now this entry's third sub-item, because it is the
          // same page.
          href: paymentsSubItems[0]?.href ?? paymentsHref,
          label: t(keys.payments.navLabel),
          icon: <PaymentsSidebarIcon />,
          isActive: onPayments,
          subItems: paymentsSubItems,
          // The whole page rides on PAYMENT_VIEW now — wallet tabs included.
          permission: "PAYMENT_VIEW",
        },
      ],
    },
    {
      header: t(keys.seller.navGroupCatalog),
      items: [
        {
          href: categoriesHref,
          label: t(keys.seller.categoriesTitle),
          icon: <CategoriesSidebarIcon />,
          isActive: isCategoriesActive ?? false,
          showHint: overview ? !overview.hasCategories : false,
          permission: "CATEGORY_VIEW",
        },
        {
          href: couponsHref,
          label: t(keys.promotions.navLabel),
          icon: <CouponsSidebarIcon />,
          isActive: isCouponsActive || isDiscountsActive,
          subItems: promotionsSubItems,
          permission: "COUPON_VIEW",
        },
        {
          href: reviewsHref,
          label: t(keys.reviews.navLabel),
          icon: <ReviewsSidebarIcon />,
          isActive: isReviewsActive,
          permission: "REVIEW_VIEW",
        },
      ],
    },
    {
      // No "قنوات البيع" heading (B8): settings, the plan and sharing are not
      // sales channels, and the phone has no such group to match.
      items: [
        // No "Store editor" row here any more. The editor is where you EDIT a
        // design, and you arrive at it by choosing one: Store Settings →
        // Design → «عدّل» on the applied card. Two doors to one room, one of
        // them unexplained, is what round 2 objected to in the first place —
        // and `apps/restaurant`, which this surface was modelled on, has never
        // had a nav entry for its editor either.
        {
          href: metaHref,
          label: t(keys.meta.title),
          icon: <ShareSidebarIcon />,
          isActive: pathname?.startsWith(metaHref) ?? false,
          permission: "STORE_VIEW",
        },
        {
          href: storeSettingsHref,
          label: t(keys.seller.storeSettingsTitle),
          icon: <SettingsSidebarIcon />,
          isActive: onSettings,
          subItems: settingsSubItems,
          // Show the settings entry if the member can reach at least one section.
          permission: settingsSubItems.length > 0 ? "STORE_VIEW" : "STORE_EDIT",
        },
        {
          // "Your plan": what the store can do, what Premium adds and costs.
          // Every admitted member can open it — on Free it is the only place
          // that says Premium exists (see shared/access/store-access.ts).
          href: planHref,
          label: t(keys.plan.navLabel),
          icon: <PlanSidebarIcon />,
          isActive: pathname?.startsWith(planHref) ?? false,
          permission: "STORE_VIEW",
        },
        {
          // An ACTION, not a destination: no `href`, so Sidebar renders it as a
          // <button> that opens the share sheet. Never "active" — no URL.
          onClick: () => setIsShareOpen(true),
          label: t(keys.share.storeTitle),
          icon: <ShareSidebarIcon />,
          isActive: false,
          // Sharing the public storefront link needs no edit rights — anyone
          // who can see the store can hand out its URL.
          permission: "STORE_VIEW",
        },
      ],
    },
  ];

  const sidebarGroups: SidebarGroup[] = rawGroups
    .map((group) => ({
      ...group,
      items: group.items
        .filter((item) => (!isIosCompanionApp() || item.href !== planHref) && (item.permission === null || can(item.permission)) && canVisit(item.href))
        .map(({ permission: _permission, ...item }) => item),
    }))
    .filter((group) => group.items.length > 0);

  const activeNavItem =
    sidebarGroups
      .flatMap((group) => group.items)
      .find((item) => item.isActive) ?? null;

  const bottomNavTabs = useBottomNavTabs({
    metaHref,
    isMetaActive: pathname?.startsWith(metaHref) ?? false,
    dashboardHref,
    ordersHref,
    cartsHref,
    customersHref,
    transactionsHref,
    paymentsHref,
    productsHref,
    categoriesHref,
    couponsHref,
    discountsHref,
    freeShippingHref,
    reviewsHref,
    // Settings → the merged hub (store settings + notifications, theme,
    // language, logout, store switch), not just the store-settings page.
    settingsHref: accountHref,
    // Tier 1 on the phone too — the desktop sidebar's Orders badge number.
    ordersCount: actionableOrderCount,
    isDashboardActive: isDashboardActive ?? false,
    isOrdersActive: isOrdersActive ?? false,
    isCartsActive,
    isCustomersActive,
    isTransactionsActive,
    isPaymentsActive,
    isProductsActive: isProductsActive ?? false,
    isCategoriesActive: isCategoriesActive ?? false,
    isCouponsActive,
    isDiscountsActive,
    isFreeShippingActive,
    isReviewsActive,
    isSettingsActive:
      (pathname?.startsWith(accountHref) ?? false) || onSettings,
    productsShowHint: overview && can("PRODUCT_CREATE") ? !overview.hasProducts : false,
    categoriesShowHint: overview ? !overview.hasCategories : false,
    // Payment setup lives under Settings.
    settingsShowHint: overview && sessionData?.seller.access?.capabilities.manualPayments === true ? !(overview.hasActivePaymentOptions || overview.acceptsCod) : false,
  });

  // Same single gate as the sidebar entry these two rows mirror. Both open
  // `/payments` (on different tabs) and both ride on PAYMENT_VIEW — the
  // wallet's former STORE_EDIT check is deliberately gone, not kept alongside.
  const visibleBottomNavTabs = bottomNavTabs.filter((tab) => {
    if (!canVisit(tab.href)) return false;
    if (tab.key === "transactions" || tab.key === "payments") {
      return can("PAYMENT_VIEW");
    }
    return true;
  });

  const handleSessionExpiryConfirm = () => {
    if (didRedirectToLoginRef.current) {
      return;
    }
    didRedirectToLoginRef.current = true;
    setIsSessionExpiryDialogOpen(false);
    navigate(sellerLoginPath, { replace: true });
  };

  const avatarInitial = (storeIdentity?.storeName ?? storeSlug ?? "?")
    .trim()
    .charAt(0)
    .toUpperCase();
  // The account avatar is the PERSON: "منى عبد الرحمن" gets "م", not the
  // store's "ب" (بيت منى…). The store switcher keeps the store's initial.
  const sellerInitial =
    (sessionData?.seller?.name ?? "").trim().charAt(0).toUpperCase() ||
    avatarInitial;

  const { mutateAsync: sellerLogout } = useLogoutMutation();
  const handleLocaleChange = (next: "en" | "ar") => {
    if (next === locale) return;
    const path = pathname ?? "/";
    const localePattern = new RegExp(`^/(${locales.join("|")})(/|$)`);
    const nextPath = localePattern.test(path)
      ? path.replace(localePattern, `/${next}$2`)
      : `/${next}${path.startsWith("/") ? "" : "/"}${path}`;
    navigate(nextPath, { replace: true });
  };
  const handleLogout = async () => {
    await sellerLogout({});
    navigate(loginUrl ?? sellerLoginPath, { replace: true });
  };

  return (
    <PageChromeProvider>
      <FcmRegistrationProvider
        sellerId={sessionData?.seller?.id}
        storeId={sessionData?.seller?.storeId}
      >
        <div className="flex h-dvh w-full flex-col overflow-hidden bg-[color:var(--canvas)] p-0">
          <PwaHead />
          <PwaBootstrap />
          <FcmBootstrap
            storeId={sessionData?.seller?.storeId}
            storeSlug={storeSlug}
          />
          <PullToRefreshHost scrollRef={scrollContainerRef} />

          {/* Body row: sidebar (desktop) + content column */}
          <div className="flex min-h-0 flex-1 overflow-hidden">
            {!isMobile ? (
              <Sidebar
                // The three props the shared sidebar takes instead of reaching
                // for this app's router, catalogue and brand mark itself.
                renderLink={({ href, children, ...rest }) => (
                  <Link to={href} {...rest}>
                    {children}
                  </Link>
                )}
                labels={{
                  appName: t(keys.common.appName),
                  collapse: t(keys.seller.navCollapse),
                  hintSetup: t(keys.seller.navHintSetup),
                  storeLive: t(keys.seller.storeLiveLabel),
                  storeSettings: t(keys.seller.storeSettingsTitle),
                  switchStore: t(keys.seller.storeSwitcherSwitch),
                  currentStore: t(keys.seller.storeSwitcherCurrent),
                }}
                logo={
                  <img
                    src={resolvedDark ? "/logo-sidebar-dark.svg" : "/logo-sidebar.svg"}
                    alt={t(keys.common.appName)}
                    className="h-7 w-auto object-contain"
                  />
                }
                dashboardHref={dashboardHref}
                settingsHref={storeSettingsHref}
                // Nav counts in the portal's digits (Arabic-Indic in ar).
                locale={locale}
                groups={sidebarGroups}
                hidden={sidebarCollapsed}
                onHide={toggleSidebar}
                identity={{
                  storeName: storeIdentity?.storeName ?? storeSlug,
                  storeShort: storeIdentity?.storeShort ?? null,
                  initial: avatarInitial,
                  logoUrl: storeData?.logoUrl ?? null,
                }}
                availableStores={(sessionData?.availableStores ?? []).filter(store => store.storeAccess === true)}
                activeStoreSlug={storeSlug}
                onSwitchStore={(slug) => void switchStore(slug)}
                isSwitchingStore={isSwitchingStore}
                accountSlot={
                  <AccountMenu
                    storeName={storeIdentity?.storeName ?? storeSlug}
                    storeShort={storeIdentity?.storeShort ?? null}
                    storeCode={storeIdentity?.storeShort ?? storeSlug}
                    sellerName={sessionData?.seller?.name ?? storeSlug}
                    sellerEmail={sessionData?.seller?.email ?? ""}
                    initial={sellerInitial}
                    locale={locale}
                    onLocaleChange={handleLocaleChange}
                    isDark={resolvedDark}
                    onThemeChange={setThemePref}
                    onPersonalInfo={() => setAccountModal("personal-info")}
                    onPassword={() => setAccountModal("password")}
                    onMyAccount={() => navigate(accountHref)}
                    onLogout={() => void handleLogout()}
                    labels={{
                      storeMenu: t(keys.seller.accountTitle),
                      live: t(keys.seller.storeLiveLabel),
                      copyCode: t(keys.seller.copyStoreLink),
                      codeCopied: t(keys.seller.linkCopiedTooltip),
                      storeShortTooltip: t(keys.seller.storeShortTooltip),
                      language: t(keys.common.language),
                      theme: t(keys.common.theme),
                      themeLight: t(keys.common.themeLight),
                      themeDark: t(keys.common.themeDark),
                      themeSystem: t(keys.common.themeSystem),
                      personalInfo: t(keys.seller.profileSettings.personalInfo),
                      password: t(keys.seller.profileSettings.password),
                      myAccount: t(keys.seller.navMyAccount),
                      logout: t(keys.common.logout),
                    }}
                  />
                }
              />
            ) : null}

            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-[color:var(--canvas)]">
              {/* Desktop: dark chrome top bar — collapse toggle + breadcrumb + tools.
                Mobile: no global header; each screen renders its own contextual bar. */}
              {!isMobile ? (
                <div className="shrink-0 overflow-hidden bg-[color:var(--card)]">
                  <header className="flex h-16 shrink-0 items-center gap-3 border-b border-[color:var(--line)] bg-[color:var(--card)] px-6 text-[color:var(--text-primary)]">
                    {sidebarCollapsed ? (
                      <button
                        type="button"
                        onClick={toggleSidebar}
                        aria-label={t(keys.seller.navExpand)}
                        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] border border-[color:var(--line)] bg-[color:var(--card)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--hover)] hover:text-[color:var(--text-primary)]"
                      >
                        <PanelIcon />
                      </button>
                    ) : null}
                    <TopBarLabel
                      fallbackIcon={
                        activeNavItem?.icon ?? <DashboardSidebarIcon />
                      }
                      fallbackLabel={
                        activeNavItem?.label ?? t(keys.seller.navControlPanel)
                      }
                    />
                    <div className="ms-auto flex items-center gap-2">
                      <NavbarSearch locale={locale} storeSlug={storeSlug} />
                      <NotificationBell
                        locale={locale}
                        storeSlug={storeSlug}
                        triggerClassName="h-[38px] w-[38px] rounded-[11px] border border-[color:var(--line)] bg-[color:var(--card)] text-[color:var(--text-secondary)] hover:bg-[color:var(--hover)] hover:text-[color:var(--text-primary)]"
                      />
                    </div>
                  </header>
                </div>
              ) : null}

              <main
                ref={scrollContainerRef}
                // The page-help tour's opening step spotlights this whole
                // region: "here is what this screen is" before "here is what
                // this control does".
                data-tour="page-main"
                className="no-scrollbar flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto overscroll-y-contain"
                data-scroll-container="portal"
              >
                <div className="w-full">
                  <HeroRegion />
                  <NotificationPromptBanner />
                  <InstallPrompt />
                  {children}
                  {/* Clearance for the floating mobile nav pill (fixed, ~76px + safe area). */}
                  <div className="h-28 lg:h-6" aria-hidden="true" />
                </div>
              </main>
            </div>
          </div>

          {isMobile ? (
            <BottomNav
              tabs={visibleBottomNavTabs}
              identity={{
                storeName: storeIdentity?.storeName ?? storeSlug,
                initial: avatarInitial,
                logoUrl: storeData?.logoUrl ?? null,
                href: accountHref,
              }}
              locale={locale}
              storeSlug={storeSlug}
              onShareStore={() => setIsShareOpen(true)}
            />
          ) : null}

          {/* Store share sheet — the single share implementation (ShareMenu),
              driven in controlled mode from the nav entry on both surfaces. */}
          <ShareMenu
            url={storeUrl}
            messageKey={keys.share.storeMessage}
            messageValues={{ store: storeIdentity?.storeName ?? storeSlug }}
            title={t(keys.share.storeTitle)}
            triggerAriaLabel={t(keys.share.storeAria)}
            open={isShareOpen}
            onClose={() => setIsShareOpen(false)}
            readiness={shareReadiness}
          />


          <ConfirmDialog
            isOpen={isSessionExpiryDialogOpen}
            title={t(keys.seller.sessionExpiredTitle)}
            body={t(keys.seller.sessionExpiredBody)}
            confirmLabel={t(keys.common.login)}
            showCancelButton={false}
            onConfirm={handleSessionExpiryConfirm}
          />

          <PersonalInfoModal
            isOpen={accountModal === "personal-info"}
            onClose={() => setAccountModal(null)}
            storeSlug={storeSlug}
            seller={sessionData?.seller ?? null}
          />
          <PasswordModal
            isOpen={accountModal === "password"}
            onClose={() => setAccountModal(null)}
            storeSlug={storeSlug}
          />
        </div>
      </FcmRegistrationProvider>
    </PageChromeProvider>
  );
}

// Light canvas page-header at the top of the content area (DESIGN_CONTEXT §8).
// Desktop top-bar label. Renders the page-specific breadcrumb when a page has
// set one (e.g. "Products › {name}" on the edit page), otherwise the active nav
// item's icon + label. Lives in its own component so it can read the chrome
// value context from inside the provider.
function TopBarLabel({
  fallbackIcon,
  fallbackLabel,
}: {
  fallbackIcon: React.ReactNode;
  fallbackLabel: React.ReactNode;
}) {
  const { breadcrumb } = usePageChromeValues();
  if (breadcrumb) {
    return (
      <div className="flex min-w-0 items-center text-[15px] font-semibold text-[color:var(--text-primary)]">
        {breadcrumb}
      </div>
    );
  }
  return (
    <div className="flex min-w-0 items-center gap-2 text-[15px] font-semibold text-[color:var(--text-primary)]">
      <span
        aria-hidden="true"
        className="inline-flex shrink-0 [&_svg]:h-[19px] [&_svg]:w-[19px]"
      >
        {fallbackIcon}
      </span>
      <span className="truncate">{fallbackLabel}</span>
    </div>
  );
}

function HeroRegion() {
  const { hero, heroAux } = usePageChromeValues();
  if (!hero && !heroAux) return null;
  return (
    <div className="w-full px-4 pt-4 sm:px-5 sm:pt-5 lg:px-7">
      {hero}
      {heroAux ? <div className="mt-4">{heroAux}</div> : null}
    </div>
  );
}

// ─── Top-bar icons ────────────────────────────────────────────────────────────

function PanelIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <line x1="9" y1="4" x2="9" y2="20" />
    </svg>
  );
}
